import axios from 'axios';
import type {
  ApiInvoiceListResponse,
  ApiInvoiceSummary,
  BlockingInvoice,
} from '@/models/invoiceTypes';

const mapInvoice = (invoice: ApiInvoiceSummary): BlockingInvoice => ({
  id: invoice.id ?? '',
  reference: invoice.reference || null,
});

/**
 * Every invoice under the client sitting in Manage Invoices unpaid or partly paid.
 *
 * `state=open` is the whole predicate: `TimeSheetStatus.Open` means "in QuickBooks,
 * balance outstanding", and `Closed` — paid in full — is deliberately not asked for,
 * because a paid invoice and its payments stay assigned to the subcustomer they were
 * written against and nothing later has to resolve them.
 *
 * Client-scoped rather than matter-scoped for the same reason the settlement lookup
 * is: `billClient` lives on the client, so one toggle moves every matter under it.
 * Split-billing invoices are caught by this too — each split carries the same
 * `ClientId` even though its matters differ.
 */
export const getOpenInvoicesForClient = async (
  clientId: string,
): Promise<BlockingInvoice[]> => {
  const { data } = await axios.get<ApiInvoiceListResponse>('/invoices', {
    params: { clientId, state: 'open', active: true },
  });
  return (data?.invoices ?? []).map(mapInvoice);
};
